import { fetchApi } from '@/services/api-client';

export type MeasurementAuditStudent = {
  id: string;
  fullName: string;
  nickname: string | null;
  /** Together with `fullName`, `gradeLevel` and `photoUrl` this row is a `StudentIdentity`. */
  displayName: string;
  photoUrl: string;
  gradeLevel: number | null;
  countsByYear: Record<string, number>;
  total: number;
  lastMeasuredAt: string | null;
  missingCurrentYear: boolean;
};

export type MeasurementAudit = {
  years: { id: string; name: string }[];
  currentYearId: string | null;
  summary: { totalStudents: number; measuredThisYear: number; missingThisYear: number; neverMeasured: number };
  students: MeasurementAuditStudent[];
};

export type TemperatureAuditStudent = {
  id: string;
  fullName: string;
  nickname: string | null;
  /** Together with `fullName`, `gradeLevel` and `photoUrl` this row is a `StudentIdentity`. */
  displayName: string;
  photoUrl: string;
  gradeLevel: number | null;
  className: string;
  readings: number;
  values: number[];
  none: boolean;
  short: boolean;
  /** Away from school that day — shown, but never counted as a missing reading. */
  outToday: boolean;
};

export type TemperatureAudit = {
  date: string;
  expected: number;
  summary: { tracked: number; none: number; short: number; complete: number; out: number };
  students: TemperatureAuditStudent[];
};

export type AllergyAuditStudent = {
  id: string;
  fullName: string;
  nickname: string | null;
  /** Together with `fullName`, `gradeLevel` and `photoUrl` this row is a `StudentIdentity`. */
  displayName: string;
  photoUrl: string;
  gradeLevel: number | null;
  allergies: { label: string; severe: boolean }[];
  dietaryNote: string | null;
  severity: 'severe' | 'mild' | 'none';
  hasRecord: boolean;
};

export type AllergyAudit = {
  summary: { totalStudents: number; severe: number; anyAllergy: number; noRecord: number };
  students: AllergyAuditStudent[];
};

export type MedicalTodayClass = {
  id: string;
  name: string;
  gradeLevel: number;
  studentCount: number;
  sick: { studentId: string; name: string; type: string; reason: string | null }[];
  temperature: { students: number; complete: number; none: number; expected: number } | null;
  infirmary: { visitId: string; studentId: string; name: string; typeLabel: string | null }[];
};

export type MedicalToday = {
  date: string;
  /** False on a weekend, a holiday or a closure — there is no temperature round to be behind on. */
  isSchoolDay: boolean;
  totals: { sick: number; infirmary: number; temperatureNone: number };
  classes: MedicalTodayClass[];
};

export type GrowthFinding = {
  indicator: 'height-for-age' | 'weight-for-age' | 'bmi-for-age';
  category: string;
  severity: 'monitor' | 'attention';
  percentile: number | null;
};

export type GrowthAuditStudent = {
  id: string;
  displayName: string;
  fullName: string;
  photoUrl: string;
  gradeLevel: number | null;
  measuredAt: string;
  weightKg: number;
  heightCm: number;
  bmi: number;
  findings: GrowthFinding[];
};

export type GrowthUnreadableStudent = {
  id: string;
  displayName: string;
  fullName: string;
  photoUrl: string;
  gradeLevel: number | null;
  issue: 'no-date-of-birth' | 'no-sex' | 'age-implausible';
  ageYears: number | null;
};

export type GrowthAudit = {
  attention: GrowthAuditStudent[];
  monitor: GrowthAuditStudent[];
  unreadable: GrowthUnreadableStudent[];
  summary: { measured: number; attention: number; monitor: number; unreadable: number };
};

export const medicalHubApi = {
  today: (date: string) => fetchApi<MedicalToday>(`/medical/hub/today?date=${encodeURIComponent(date)}`),
  measurements: () => fetchApi<MeasurementAudit>('/medical/audit/measurements'),
  temperature: (date: string) =>
    fetchApi<TemperatureAudit>(`/medical/audit/temperature?date=${encodeURIComponent(date)}`),
  allergies: () => fetchApi<AllergyAudit>('/medical/audit/allergies'),
  growth: () => fetchApi<GrowthAudit>('/medical/audit/growth'),
};
